var KEYCODE_TO_CODE = require("../constants").keyboardevent.KEYCODE_TO_CODE;
var registerComponent = require("../core/component").registerComponent;
var THREE = require("../lib/three");
var utils = require("../utils/");

var bind = utils.bind;
var shouldCaptureKeyEvent = utils.shouldCaptureKeyEvent;

var CLAMP_VELOCITY = 0.00001;
var MAX_DELTA = 0.2;
var KEYS = ["KeyW", "KeyA", "KeyS", "KeyD", "KeyX", "KeyC"];

/**
 * WASD component to control entities using WASD keys.
 */
module.exports.Component = registerComponent("wasd-controls", {
  schema: {
    acceleration: { default: 65 },
    adAxis: { default: "x", oneOf: ["x", "y", "z"] },
    adEnabled: { default: true },
    adInverted: { default: false },
    easing: { default: 20 },
    enabled: { default: true },
    fly: { default: true },
    wsAxis: { default: "z", oneOf: ["x", "y", "z"] },
    wsEnabled: { default: true },
    xcAxis: { default: "y", oneOf: ["x", "y", "z"] },
    xcEnabled: { default: true },
    wsInverted: { default: false },
  },

  init: function () {
    // To keep track of the pressed keys.
    this.keys = {};
    this.velocity = new THREE.Vector3();
    this.dir = new THREE.Vector3();

    // Bind methods and add event listeners.
    this.onBlur = bind(this.onBlur, this);
    this.onFocus = bind(this.onFocus, this);
    this.onKeyDown = bind(this.onKeyDown, this);
    this.onKeyUp = bind(this.onKeyUp, this);
    this.onVisibilityChange = bind(this.onVisibilityChange, this);
    this.attachVisibilityEventListeners();
  },

  tick: function (time, delta) {
    var data = this.data;
    var el = this.el;
    var velocity = this.velocity;
    var dir = this.dir;

    if (
      !velocity[data.adAxis] &&
      !velocity[data.wsAxis] &&
      !velocity[data.xcAxis] &&
      isEmptyObject(this.keys)
    ) {
      return;
    }

    // Update velocity.
    delta = delta / 1000;
    this.updateVelocity(delta);

    if (
      !velocity[data.adAxis] &&
      !velocity[data.wsAxis] &&
      !velocity[data.xcAxis]
    ) {
      return;
    }

    // Get movement vector and translate position.
    // el.object3D.getWorldDirection(dir);
    // el.object3D.position.add( dir.multiply(velocity));
    // el.object3D.position.addScaledVector(dir, velocity[data.wsAxis] * delta);

    const movement = this.getMovementVector(delta);
    el.object3D.position.y += movement.y;
    el.object3D.position.x += movement.x;
    el.object3D.position.z += movement.z;
  },

  remove: function () {
    this.removeKeyEventListeners();
    this.removeVisibilityEventListeners();
  },

  play: function () {
    this.attachKeyEventListeners();
  },

  pause: function () {
    this.keys = {};
    this.removeKeyEventListeners();
  },

  updateVelocity: function (delta) {
    var acceleration;
    var adAxis;
    var adSign;
    var data = this.data;
    var keys = this.keys;
    var velocity = this.velocity;
    var wsAxis;
    var wsSign;
    var xcAxis;

    adAxis = data.adAxis;
    wsAxis = data.wsAxis;
    xcAxis = data.xcAxis;

    // If FPS too low, reset velocity.
    if (delta > MAX_DELTA) {
      velocity[adAxis] = 0;
      velocity[wsAxis] = 0;
      velocity[xcAxis] = 0;
      return;
    }

    // Decay velocity.
    if (velocity[adAxis] !== 0) {
      velocity[adAxis] -= velocity[adAxis] * data.easing * delta;
    }
    if (velocity[wsAxis] !== 0) {
      velocity[wsAxis] -= velocity[wsAxis] * data.easing * delta;
    }
    if (velocity[xcAxis] !== 0) {
      velocity[xcAxis] -= velocity[xcAxis] * data.easing * delta;
    }
    // Clamp velocity easing.
    if (Math.abs(velocity[adAxis]) < CLAMP_VELOCITY) {
      velocity[adAxis] = 0;
    }
    if (Math.abs(velocity[wsAxis]) < CLAMP_VELOCITY) {
      velocity[wsAxis] = 0;
    }
    if (Math.abs(velocity[xcAxis]) < CLAMP_VELOCITY) {
      velocity[xcAxis] = 0;
    }

    if (!data.enabled) {
      return;
    }

    // Update velocity using keys pressed.
    acceleration = data.acceleration;
    if (data.adEnabled) {
      adSign = data.adInverted ? -1 : 1;
      if (keys.KeyA || keys.ArrowLeft) {
        velocity[adAxis] -= adSign * acceleration * delta;
      }
      if (keys.KeyD || keys.ArrowRight) {
        velocity[adAxis] += adSign * acceleration * delta;
      }
    }
    if (data.wsEnabled) {
      wsSign = data.wsInverted ? -1 : 1;
      if (keys.KeyW || keys.ArrowUp) {
        velocity[wsAxis] -= wsSign * acceleration * delta;
      }
      if (keys.KeyS || keys.ArrowDown) {
        velocity[wsAxis] += wsSign * acceleration * delta;
      }
    }

    if (keys.KeyX) {
      velocity[xcAxis] -= acceleration * delta;
    }
    if (keys.KeyC) {
      velocity[xcAxis] += acceleration * delta;
    }
  },

  getMovementVector: (function () {
    var directionVector = new THREE.Vector3(0, 0, 0);
    var rotationEuler = new THREE.Euler(0, 0, 0, "YXZ");

    return function (delta) {
      var rotation = this.el.getAttribute("rotation");
      var velocity = this.velocity;
      var xRotation;

      directionVector.copy(velocity);
      directionVector.multiplyScalar(delta);

      // Absolute.
      if (!rotation) {
        return directionVector;
      }

      xRotation = this.data.fly ? rotation.x : 0;

      // Transform direction relative to heading.
      rotationEuler.set(
        THREE.Math.degToRad(xRotation),
        THREE.Math.degToRad(rotation.y),
        THREE.Math.degToRad(rotation.z)
      );
      directionVector.applyEuler(rotationEuler);
      return directionVector;
    };
  })(),

  attachVisibilityEventListeners: function () {
    window.addEventListener("blur", this.onBlur);
    window.addEventListener("focus", this.onFocus);
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  },

  removeVisibilityEventListeners: function () {
    window.removeEventListener("blur", this.onBlur);
    window.removeEventListener("focus", this.onFocus);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
  },

  attachKeyEventListeners: function () {
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
  },

  removeKeyEventListeners: function () {
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
  },

  onBlur: function () {
    this.pause();
  },

  onFocus: function () {
    this.play();
  },

  onVisibilityChange: function () {
    if (document.hidden) {
      this.onBlur();
    } else {
      this.onFocus();
    }
  },

  onKeyDown: function (event) {
    var code;
    if (!shouldCaptureKeyEvent(event)) {
      return;
    }
    code = event.code || KEYCODE_TO_CODE[event.keyCode];
    if (KEYS.indexOf(code) !== -1) {
      this.keys[code] = true;
    }
  },

  onKeyUp: function (event) {
    var code;
    code = event.code || KEYCODE_TO_CODE[event.keyCode];
    delete this.keys[code];
  },
});

function isEmptyObject(keys) {
  var key;
  for (key in keys) {
    return false;
  }
  return true;
}
